import { useCallback, useEffect, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceOfferQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyEngineId, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import { logger } from 'utils/logger';

import { mapServiceOffer } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

export function useLoyaltyServiceOffer({
  loyaltyEngineId,
  skip = false,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  skip?: boolean;
}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const querySkip = skip || !loyaltyEngineId || loyaltyUserIdLoading;

  const { data, error, loading: queryLoading, refetch } = useLoyaltyServiceOfferQuery({
    variables: {
      id: loyaltyEngineId,
      serviceMode: getLoyaltyServiceServiceMode(serviceMode),
      storeId: store?.number,
      redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
      includeOfferGroups: true,
      omitInvalids: false,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: querySkip,
  });

  const selectedIncentive = useMemo(() => {
    const serviceOffer = data?.loyaltyServiceOffer;
    return serviceOffer ? appOfferToLoyaltyOffer(mapServiceOffer(serviceOffer)) : undefined;
  }, [data]);

  useEffect(() => {
    if (error) {
      logger.warn({ error, message: 'Failed to load loyalty-service offer', loyaltyEngineId });
    }
  }, [error, loyaltyEngineId]);

  const refreshOffer = useCallback(async () => {
    if (querySkip) {
      return;
    }
    try {
      await refetch();
    } catch (error) {
      logger.warn({ error, message: 'Failed to load loyalty-service offer', loyaltyEngineId });
    }
  }, [loyaltyEngineId, querySkip, refetch]);

  const isIncentivesLoading =
    !skip && (loyaltyUserIdLoading || Boolean(loyaltyEngineId && queryLoading));

  return {
    selectedIncentive,
    isIncentivesLoading,
    refreshOffer,
  };
}
